import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { FaInstagram, FaYoutube, FaTwitter, FaLinkedin, FaFacebook, FaPinterest, FaMapMarkerAlt } from 'react-icons/fa';
import '../insight.css';

const InfluencerDashboard = () => {
  const { idAndSlug } = useParams();
  const location = useLocation();
  
  // Get the influencer data from location state
  const influencerData = location.state?.influencerData;
  
  // If no data was passed (direct URL access), fetch from JSON
  const [influencer, setInfluencer] = useState(influencerData);
  const [loading, setLoading] = useState(!influencerData);

  useEffect(() => {
    if (!influencerData) {
      // Extract ID from URL
      const id = idAndSlug.split('-')[0];
      
      // Fetch the specific influencer data
      fetch('/data/influencers.json')
        .then(response => response.json())
        .then(data => {
          const foundInfluencer = data.find(item => item.id === parseInt(id));
          setInfluencer(foundInfluencer);
          setLoading(false);
        });
    }
  }, [idAndSlug, influencerData]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!influencer) {
    return <div>Influencer not found</div>;
  }

  // Helper function to render progress bars
  const renderProgressBars = (data) => {
    return data.map((item) => {
      const key = Object.keys(item)[0] === 'range' ? item.range : item.gender || item.location;
      const value = item.percentage;
      return (
        <div className="progress-container" key={key}>
          <div className="progress-label">
            <span>{key}</span>
            <span>{value}</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: value }}></div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="insightcontainer">
      <header>
        <div className="header-content">
          <div>
            <h1>{influencer.influencerData.name} <span className="username">{influencer.influencerData.username}</span></h1>
            <p className="bio">{influencer.influencerData.bio}</p>
            <p><FaMapMarkerAlt /> {influencer.influencerData.location}</p>
          </div>
          <img src={influencer.influencerData.image} alt="Profile Picture" className="profile-pic" />
        </div>
      </header>
      
      <section>
        <h2>Basic Information</h2>
        <div className="card">
          <div className="grid">
            <div>
              <h3>Personal Details</h3>
              <p><strong>Full Name:</strong> {influencer.personalDetails.fullName}</p>
              <p><strong>Date of Birth:</strong> {influencer.personalDetails.dateOfBirth}</p>
              <p><strong>Location:</strong> {influencer.personalDetails.location}</p>
              <p><strong>Languages:</strong> {influencer.personalDetails.languages.join(', ')}</p>
            </div>
            <div>
              <h3>About</h3>
              <p>{influencer.personalDetails.about}</p>
              <p><strong>Website:</strong> <a href={influencer.personalDetails.website} target="_blank" rel="noopener noreferrer">{influencer.personalDetails.website}</a></p>
            </div>
          </div>
        </div>
      </section>
      
      <section>
        <h2>Social Media Accounts</h2>
        <div className="card">
          <div className="grid">
            <div>
              <h3>Platforms</h3>
              {influencer.socialMedia.platforms.map(platform => {
                let icon;
                switch(platform.name.toLowerCase()) {
                  case 'instagram': icon = <FaInstagram className="instagram" />; break;
                  case 'youtube': icon = <FaYoutube className="youtube" />; break;
                  case 'twitter': icon = <FaTwitter className="twitter" />; break;
                  case 'linkedin': icon = <FaLinkedin className="linkedin" />; break;
                  case 'facebook': icon = <FaFacebook className="facebook" />; break;
                  case 'pinterest': icon = <FaPinterest className="pinterest" />; break;
                  default: icon = null;
                }
                return (
                  <a 
                    key={platform.name} 
                    href={platform.url} 
                    className="social-badge" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {icon} {platform.handle}
                  </a>
                );
              })}
            </div>
            <div>
              <h3>Follower Count</h3>
              <div className="grid" style={{gridTemplateColumns: 'repeat(2, 1fr)'}}>
                {Object.entries(influencer.socialMedia.followerCounts).map(([platform, count]) => (
                  <div className="stat-card" key={platform}>
                    <div className="stat-value">{count}</div>
                    <div className="stat-label">{platform}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section>
        <h2>Audience Demographics</h2>
        <div className="card">
          <div className="grid">
            <div>
              <h3>Age Range</h3>
              {renderProgressBars(influencer.audienceDemographics.ageRange)}
            </div>
            <div>
              <h3>Gender Breakdown</h3>
              {renderProgressBars(influencer.audienceDemographics.genderBreakdown)}
            </div>
            <div>
              <h3>Top Locations</h3>
              {renderProgressBars(influencer.audienceDemographics.topLocations)}
            </div>
          </div>
          
          <h3>Audience Interests</h3>
          <div>
            {influencer.audienceDemographics.audienceInterests.map(interest => (
              <span key={interest} className="tag">{interest}</span>
            ))}
          </div>
        </div>
      </section>
      
      <section>
        <h2>Content Categories</h2>
        <div className="card">
          <div className="grid">
            <div>
              <h3>Primary Niche</h3>
              <p><strong>{influencer.contentCategories.primaryNiche.name}</strong></p>
              <p>{influencer.contentCategories.primaryNiche.description}</p>
            </div>
            <div>
              <h3>Secondary Niche</h3>
              <p><strong>{influencer.contentCategories.secondaryNiche.name}</strong></p>
              <p>{influencer.contentCategories.secondaryNiche.description}</p>
            </div>
          </div>
          
          <h3>Content Types</h3>
          <div>
            {influencer.contentCategories.contentTypes.map(type => (
              <span key={type} className="tag">{type}</span>
            ))}
          </div>
        </div>
      </section>
      
      <section>
        <h2>Brand Collaborations</h2>
        <div className="card">
          <h3>Brands Worked With</h3>
          <div>
            {influencer.brandCollaborations.brandsWorkedWith.map(brand => (
              <span key={brand} className="tag">{brand}</span>
            ))}
          </div>
          
          <h3>Recent Campaigns</h3>
          <div className="grid">
            {influencer.brandCollaborations.recentCampaigns.map((campaign, index) => (
              <div key={index} className="campaign-card">
                <h4>{campaign.brand}</h4>
                <p><strong>Type:</strong> {campaign.type}</p>
                <p><strong>Platform:</strong> {campaign.platform}</p>
                <p><strong>Results:</strong> {campaign.results}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section>
        <h2>Performance Metrics</h2>
        <div className="card">
          <div className="grid">
            {Object.entries(influencer.performanceMetrics).map(([platform, metrics]) => (
              <div key={platform} className="metrics-card">
                <h3>{platform}</h3>
                <div className="metrics-grid">
                  {Object.entries(metrics).map(([metric, value]) => (
                    <div key={metric} className="metric-item">
                      <div className="metric-label">{metric.replace(/([A-Z])/g, ' $1').trim()}:</div>
                      <div className="metric-value">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section>
        <h2>Pricing Rates</h2>
        <div className="card">
          <h3>Standard Rates</h3>
          <div className="grid">
            {influencer.pricingRates.standardRates.map((rate, index) => (
              <div key={index} className="rate-card">
                <h4>{rate.type}</h4>
                <p className="price">{rate.price}</p>
              </div>
            ))}
          </div>
          
          <h3>Additional Options</h3>
          <div>
            {influencer.pricingRates.additionalOptions.map((option, index) => (
              <span key={index} className="tag">{option}</span>
            ))}
          </div>
        </div>
        <section>
        <h2>Content Samples</h2>
        <div className="card">
          <h3>Images</h3>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            {influencer.contentSamples?.images?.map((image, index) => (
              <img key={index} src={image} alt={`Content Sample ${index + 1}`} style={{ width: '100%', borderRadius: '8px', display: 'block' }} />
            ))}
            {influencer.contentSamples?.images?.length === 0 && <p>No content samples available.</p>}
          </div>
          {influencer.contentSamples?.portfolioLink && (
            <p><strong>Portfolio:</strong> <a href={influencer.contentSamples.portfolioLink} target="_blank" rel="noopener noreferrer">{influencer.contentSamples.portfolioLink}</a></p>
          )}
        </div>
      </section>

      <section>
        <h2>Contact Information</h2>
        <div className="card">
          <h3>Business Inquiries</h3>
          {influencer.contactInformation?.businessInquiries?.email && (
            <p><strong>Email:</strong> <a href={`mailto:${influencer.contactInformation.businessInquiries.email}`}>{influencer.contactInformation.businessInquiries.email}</a></p>
          )}
          {influencer.contactInformation?.businessInquiries?.agent && (
            <p><strong>Agent:</strong> {influencer.contactInformation.businessInquiries.agent}</p>
          )}
          {!influencer.contactInformation?.businessInquiries?.email && !influencer.contactInformation?.businessInquiries?.agent && (
            <p>No business contact information available.</p>
          )}

          <h3>Availability</h3>
          {influencer.contactInformation?.availability?.responseTime && (
            <p><strong>Response Time:</strong> {influencer.contactInformation.availability.responseTime}</p>
          )}
          {influencer.contactInformation?.availability?.dmAvailability && (
            <p><strong>DM Availability:</strong> {influencer.contactInformation.availability.dmAvailability}</p>
          )}
          {influencer.contactInformation?.availability?.bookingLeadTime && (
            <p><strong>Booking Lead Time:</strong> {influencer.contactInformation.availability.bookingLeadTime}</p>
          )}
          {!influencer.contactInformation?.availability?.responseTime &&
           !influencer.contactInformation?.availability?.dmAvailability &&
           !influencer.contactInformation?.availability?.bookingLeadTime && (
             <p>Availability information not specified.</p>
           )}
        </div>
      </section>

      <footer>
        <div className="footer-content">
          {influencer.footer?.copyright && <p>{influencer.footer.copyright}</p>}
          {influencer.footer?.lastUpdated && <p>Last Updated: {influencer.footer.lastUpdated}</p>}
        </div>
      </footer>
      </section>
    </div>
  );
};

export default InfluencerDashboard;